<template>
  <div class="v3-datatable-action-panel">
    <div
      class="d-flex align-items-center justify-content-between flex-wrap gap-4"
    >
      <div class="action-left d-flex align-items-center">
        <div
          v-if="tabs && tabs.length"
          class="action-panel-tabs"
          :role="useTabRoles ? 'tablist' : undefined"
          :aria-label="useTabRoles ? resolvedTabsAriaLabel : undefined"
        >
          <PillButton
            data-test-id="action-panel-tab"
            :tab-mode="useTabRoles"
            :tab-id="useTabRoles ? `action-panel-tab-${i}` : ''"
            :is-tab-selected="useTabRoles ? isTabSelected(tab) : false"
            :tabindex="useTabRoles ? 0 : undefined"
            :aria-controls="
              useTabRoles ? `action-panel-tabpanel-${i}` : undefined
            "
            :active="state.activeTab && state.activeTab.label === tab.label"
            :class="{
              'rounded-start': i == 0,
              'rounded-end': i == tabs.length - 1
            }"
            :show-sub-title="tab.isPremiumFeature"
            :sub-title="$t('common.premium_feature')"
            @click="setActiveTab(tab)"
            @keydown="onTabKeydown($event, i)"
            v-for="(tab, i) in tabs"
            :key="i"
            >{{ tab.label }}</PillButton
          >
        </div>
        <div
          v-if="showTopLeftSlot"
          class="action-panel-left-slot ms-1 align-self-start"
        >
          <slot name="first-top-left-content"></slot>
        </div>
        <div v-if="showSearch" class="action-panel-search ms-1">
          <label class="visually-hidden" :for="state.searchInputId">
            {{ $t("common.search") }}
          </label>
          <InputField
            data-test-id="action-panel-search-input"
            :id="state.searchInputId"
            prepend-icon="ri-search-line"
            :placeholder="$t('common.search')"
            :model-value="searchVModel"
            @update:model-value="onSearch"
          ></InputField>
        </div>
        <div v-if="showSort" class="action-panel-sort ms-1">
          <IconButton
            @click="onSort"
            :aria-label="ariaLabelForSortIconButton"
            :aria-expanded="filtersPanelExpanded"
            icon="ri-filter-line"
          ></IconButton>
        </div>
        <slot name="left-content"></slot>
        <HelpCenterButton
          v-if="helpButton"
          classes="ms-2"
          :content-key="helpButtonContentKey"
          :is-old="true"
        />
      </div>
      <div class="action-right d-flex align-items-center justify-content-end">
        <slot name="right-content"></slot>
        <div v-if="showExport" class="action-panel-sort ms-1">
          <IconButton
            @click="onExportClick"
            :icon="exportLoading ? `ri-loader-4-line` : `ri-download-2-line`"
            :class="{ loading: exportLoading }"
            :aria-label="ariaLabelForExportIconButton"
            :disabled="exportLoading"
          ></IconButton>
        </div>
        <slot name="right-last-content"></slot>
      </div>
    </div>
    <div class="action-panel-bottom-content w-100 min-w-0">
      <slot name="bottom-content"></slot>
    </div>
  </div>
</template>

<script>
import InputField from "@/v3components/shared/Form/InputField.vue"
import PillButton from "../Buttons/PillButton.vue"
import IconButton from "../Buttons/IconButton.vue"
import { reactive, onMounted, watch, computed, nextTick } from "vue"
import { useI18n } from "vue-i18n"
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"

export default {
  props: {
    tabs: {
      type: Array,
      default: null
    },
    showSearch: {
      type: Boolean,
      default: false
    },
    showSort: {
      type: Boolean,
      default: false
    },
    showExport: {
      type: Boolean,
      default: false
    },
    exportLoading: {
      type: Boolean,
      default: false
    },
    helpButton: {
      type: Boolean,
      default: false
    },
    helpButtonContentKey: {
      type: String,
      default: ""
    },
    searchVModel: {
      type: String,
      default: ""
    },
    showTopLeftSlot: {
      type: Boolean,
      default: false
    },
    selectedTabPosition: {
      type: Number,
      default: 0
    },
    ariaLabelForSortIconButton: {
      type: String,
      default: () => "Sort"
    },
    /** When set, sort (filter) button exposes expanded/collapsed state for a11y */
    filtersPanelExpanded: {
      type: Boolean,
      default: undefined
    },
    ariaLabelForExportIconButton: {
      type: String,
      default: () => "Export"
    },
    onWatchUpdateTabPositionUi: {
      type: Boolean,
      default: false
    },
    /**
     * When true, tab strip uses `tablist` / `tab` roles and roving tabindex (WCAG tabs pattern).
     * Default true; set false only if tabs are not an ARIA tab widget.
     */
    useTabRoles: {
      type: Boolean,
      default: true
    },
    /** Optional override for the tablist accessible name (otherwise i18n `common.filter_tabs`). */
    tabsAriaLabel: {
      type: String,
      default: ""
    }
  },
  emits: ["tabClicked", "onSearch", "onSort", "onExport"],
  components: { PillButton, InputField, IconButton, HelpCenterButton },
  setup(props, { emit }) {
    const { t } = useI18n()

    const state = reactive({
      activeTab: null,
      searchInputId: `action-panel-search-${Math.random().toString(36).slice(2, 10)}`
    })

    const resolvedTabsAriaLabel = computed(() =>
      props.tabsAriaLabel ? props.tabsAriaLabel : t("common.filter_tabs")
    )

    const isTabSelected = (tab) => {
      if (!state.activeTab || !tab) return false
      if (
        Object.prototype.hasOwnProperty.call(tab, "value") &&
        tab.value !== undefined &&
        tab.value !== ""
      ) {
        return state.activeTab.value === tab.value
      }
      return state.activeTab.label === tab.label
    }

    onMounted(() => {
      setTimeout(() => {
        setActiveTab(
          props.tabs ? props.tabs[props.selectedTabPosition] : state.activeTab
        )
      }, 500)
    })

    watch(
      () => props.tabs,
      (newTabs) => {
        if (newTabs && newTabs.length) {
          if (
            state.activeTab &&
            !newTabs.some((el) => el.value === state.activeTab.value)
          ) {
            setActiveTab(newTabs[0])
          }
        }
      }
    )
    watch(
      () => props.selectedTabPosition,
      (newTabPosition) => {
        if (
          props.onWatchUpdateTabPositionUi &&
          props.tabs?.length &&
          newTabPosition >= 0 &&
          newTabPosition < props.tabs.length
        ) {
          state.activeTab = props.tabs[newTabPosition]
        }
      }
    )

    const setActiveTab = (tab) => {
      if (!tab) return
      state.activeTab = tab
      emit("tabClicked", tab)
    }

    const focusTabButton = (index) => {
      nextTick(() => {
        const el = document.getElementById(`action-panel-tab-${index}`)
        el?.focus?.()
      })
    }

    const isRtlTablist = (event) => {
      const target = event?.currentTarget
      if (!target) return false
      const withDir = target.closest("[dir]")
      const dir =
        withDir?.getAttribute("dir") ||
        target.ownerDocument?.documentElement?.getAttribute("dir") ||
        ""
      return String(dir).toLowerCase() === "rtl"
    }

    const onTabKeydown = (event, currentIndex) => {
      if (!props.tabs?.length) return

      const key = event?.key || ""
      const maxIndex = props.tabs.length - 1
      let nextIndex = currentIndex
      const rtl = isRtlTablist(event)

      if (key === "ArrowRight") {
        nextIndex = rtl
          ? currentIndex <= 0
            ? maxIndex
            : currentIndex - 1
          : currentIndex >= maxIndex
            ? 0
            : currentIndex + 1
      } else if (key === "ArrowLeft") {
        nextIndex = rtl
          ? currentIndex >= maxIndex
            ? 0
            : currentIndex + 1
          : currentIndex <= 0
            ? maxIndex
            : currentIndex - 1
      } else if (key === "Home") {
        nextIndex = 0
      } else if (key === "End") {
        nextIndex = maxIndex
      } else if (key === "Enter" || key === " ") {
        event.preventDefault()
        event.stopPropagation()
        setActiveTab(props.tabs[currentIndex])
        return
      } else {
        return
      }

      event.preventDefault()
      setActiveTab(props.tabs[nextIndex])
      focusTabButton(nextIndex)
    }
    const onSearch = (value) => {
      emit("onSearch", value)
    }
    const onSort = (value) => {
      emit("onSort", value)
    }
    const onExportClick = (value) => {
      emit("onExport", value)
    }

    return {
      setActiveTab,
      onTabKeydown,
      onSearch,
      onSort,
      onExportClick,
      state,
      resolvedTabsAriaLabel,
      isTabSelected
    }
  }
}
</script>
